import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Sparkles, Edit, MessageSquare, History, Download, Trash2, Upload, AlertTriangle, Calendar, Users, MoreVertical, BarChart3, ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import ForecastVsActualChart from '../../components/forecasts/ForecastVsActualChart';
import ServiceLevelControls, { ServiceLevelParams } from '../../components/forecasts/ServiceLevelControls';
import NetStaffingGrid from '../../components/forecasts/NetStaffingGrid';
import CreateForecastModal from '../../components/forecasts/CreateForecastModal';
import ForecastShapeEditor from '../../components/forecasts/ForecastShapeEditor';
import ForecastAIAssistant from '../../components/forecasts/ForecastAIAssistant';
import CollapsibleForecastTable from '../../components/forecasts/CollapsibleForecastTable';
import { DailyAccuracyTable } from '../../components/forecasts/DailyAccuracyTable';
import { ForecastAssumptionsPanel } from '../../components/forecasts/ForecastAssumptionsPanel';
import { ForecastFactorBreakdown } from '../../components/forecasts/ForecastFactorBreakdown';
import OutlierDetectionModal from '../../components/forecasts/OutlierDetectionModal';
import HolidayManagementModal from '../../components/forecasts/HolidayManagementModal';
import ShiftGenerationModal from '../../components/forecasts/ShiftGenerationModal';
import {
  useForecasts,
  useExportForecastCSV,
  useGenerateForecast,
  useForecastDataPoints,
  useDeleteForecast,
  usePublishForecast,
  useUnpublishForecast,
  useEditForecastValues,
  useImportForecastCSV,
  useImportHistoricalCSV,
  useRegenerateAsync,
  useDailyAccuracy,
  useForecastAssumptions,
  useForecastFactorBreakdown,
  type ImportJobStatus,
  type RegenerationJobStatus,
  useCreateAssumption,
  useUpdateAssumption,
  useDeleteAssumption,
} from '../../hooks/useForecasts';
import { DateRangeEditType } from '../../services/forecasts.service';
import { useAuthStore } from '../../stores/auth.store';
import ForecastVersionHistorySidebar from '../../components/modals/ForecastVersionHistorySidebar';
import {
  generateWeeklyVolumePattern,
  generateScheduledStaffing,
} from '../../utils/mockForecastData';

type IntervalType = '15m' | '30m' | '60m';
type ChannelType = 'ALL' | 'PHONE' | 'CHAT' | 'EMAIL';
type ViewMode = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export default function ModernForecastsPage() {
  // Navigation
  const navigate = useNavigate();

  // Get user, token, and forecasts
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const { data: forecasts, isLoading: isLoadingForecasts } = useForecasts(user?.organizationId);

  // Selected forecast state
  const [selectedForecastId, setSelectedForecastId] = useState<string | null>(null);

  // Filter states - must be declared before useForecastDataPoints
  const [selectedInterval, setSelectedInterval] = useState<IntervalType>('15m');
  const [selectedChannel, setSelectedChannel] = useState<ChannelType>('ALL');
  const [selectedTeam, setSelectedTeam] = useState<string>('ALL');
  const [selectedShift, setSelectedShift] = useState<string>('ALL');
  const [viewMode, setViewMode] = useState<ViewMode>('MONTHLY');
  const [variancePercent] = useState(15);
  const [selectedDate, setSelectedDate] = useState(() => new Date());

  // Auto-select first forecast when forecasts load
  useEffect(() => {
    console.log('Forecasts loaded:', forecasts);
    console.log('Forecasts count:', forecasts?.length);

    if (forecasts && forecasts.length > 0 && !selectedForecastId) {
      console.log('Auto-selecting first forecast:', forecasts[0]);
      setSelectedForecastId(forecasts[0].id);
    }
  }, [forecasts, selectedForecastId]);

  // Fetch data points for selected forecast with filters
  const { data: dataPoints, isLoading: isLoadingDataPoints } = useForecastDataPoints(
    selectedForecastId || '',
    {
      channel: selectedChannel,
      team: selectedTeam,
      shift: selectedShift,
    }
  );

  // Fetch daily accuracy data for the selected forecast
  const { data: dailyAccuracyData, isLoading: isLoadingDailyAccuracy } = useDailyAccuracy(
    selectedForecastId || ''
  );

  // Fetch assumptions and factor breakdown for the selected forecast
  const { data: assumptions, isLoading: isLoadingAssumptions } = useForecastAssumptions(
    selectedForecastId || ''
  );
  const { data: factorBreakdown, isLoading: isLoadingFactorBreakdown } = useForecastFactorBreakdown(
    selectedForecastId || ''
  );

  const currentForecast = forecasts?.find(f => f.id === selectedForecastId);

  // Log when selected forecast changes and update selected date to forecast start date
  useEffect(() => {
    console.log('Selected forecast ID changed to:', selectedForecastId);
    console.log('Current forecast:', currentForecast);

    // When forecast changes, set selected date to forecast's start date
    // This ensures we see data for dates that actually have forecast data points
    if (currentForecast?.startDate) {
      const forecastStartDate = new Date(currentForecast.startDate);
      console.log('Setting selected date to forecast start date:', forecastStartDate);
      setSelectedDate(forecastStartDate);
    }
  }, [selectedForecastId, currentForecast]);

  // Check if user is demo account (admin@lentil-labs.com)
  const isDemoAccount = user?.email === 'admin@lentil-labs.com';

  // State for forecast data (use real data if available, otherwise mock for demo account)
  const [forecastData, setForecastData] = useState(() =>
    isDemoAccount ? generateWeeklyVolumePattern(150) : []
  );
  const [scheduledData] = useState(() =>
    isDemoAccount ? generateScheduledStaffing(0.9) : []
  );

  // Update forecast data when data points load
  useEffect(() => {
    // Skip data loading for demo account - keep using mock data
    if (isDemoAccount) {
      console.log('Demo account - using mock data');
      return;
    }

    console.log('Data points changed:', dataPoints);
    console.log('Data points length:', dataPoints?.length);

    if (dataPoints && dataPoints.length > 0) {
      console.log('First data point sample:', dataPoints[0]);

      // Transform API data points to chart format
      // IMPORTANT: Do not generate fake actual values - only use real synced data
      // Use local time so shift generation modal can match intervals correctly
      // (shifts are defined in local time, so interval keys must also be local)
      const transformedData = dataPoints.map((dp: any) => {
        const date = new Date(dp.timestamp);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return {
          interval: `${hours}:${minutes}`,
          predictedValue: Math.round(dp.predictedValue || 0),
          // Only include actual value if it exists from integration sync
          actualValue: dp.actualValue != null ? Math.round(dp.actualValue) : undefined,
          upperBound: Math.round((dp.upperBound || dp.predictedValue * 1.15)),
          lowerBound: Math.round((dp.lowerBound || dp.predictedValue * 0.85)),
          // Store as Date object for proper chart aggregation
          timestamp: date,
        };
      });

      console.log('Transformed data count:', transformedData.length);
      console.log('First transformed point:', transformedData[0]);

      setForecastData(transformedData);
    } else {
      console.log('No data points available - empty forecast data');
      setForecastData([]);
    }
  }, [dataPoints, isDemoAccount]);

  // State for modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showShapeEditor, setShowShapeEditor] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [showOutlierDetection, setShowOutlierDetection] = useState(false);
  const [showHolidayManagement, setShowHolidayManagement] = useState(false);
  const [showShiftGeneration, setShowShiftGeneration] = useState(false);
  const [isGeneratingShifts, setIsGeneratingShifts] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showAccuracySection, setShowAccuracySection] = useState(false);
  const [showAssumptionsSection, setShowAssumptionsSection] = useState(false);

  // Export CSV mutation
  const { mutate: exportCSV, isPending: isExporting } = useExportForecastCSV();

  // Generate forecast mutation
  const { mutate: generateForecast, isPending: isGenerating } = useGenerateForecast();

  // Delete forecast mutation
  const { mutate: deleteForecast, isPending: isDeleting } = useDeleteForecast();

  // Publish forecast mutation
  const { mutate: publishForecast, isPending: isPublishing } = usePublishForecast();

  // Unpublish forecast mutation
  const { mutate: unpublishForecast, isPending: isUnpublishing } = useUnpublishForecast();

  // Edit forecast values mutation
  const { mutate: editForecastValues, isPending: isEditingForecast } = useEditForecastValues();

  // Import CSV mutations
  const { mutate: importCSV, isPending: isImportingCSV } = useImportForecastCSV();

  // Import progress callback for async historical CSV import
  const handleImportProgress = useCallback((status: ImportJobStatus) => {
    console.log(`[Import Progress] ${status.progress}%: ${status.message}`);
  }, []);

  const handleRegenerationProgress = useCallback((status: RegenerationJobStatus) => {
    console.log(`[Regeneration Progress] ${status.progress}%: ${status.message}`);
  }, []);

  const { mutate: importHistoricalCSV, isPending: isImportingHistoricalCSV } = useImportHistoricalCSV(handleImportProgress);

  // Async regeneration mutation (for large datasets that would timeout synchronously)
  const { mutate: regenerateAsync } = useRegenerateAsync(handleRegenerationProgress);

  // Assumption mutations
  const { mutate: createAssumption, isPending: isCreatingAssumption } = useCreateAssumption();
  const { mutate: updateAssumption, isPending: isUpdatingAssumption } = useUpdateAssumption();
  const { mutate: deleteAssumptionMutation, isPending: isDeletingAssumption } = useDeleteAssumption();

  // Service level parameters (default: 80% in 20s, 6min AHT, 20% shrinkage)
  const [serviceLevelParams, setServiceLevelParams] = useState<ServiceLevelParams>({
    slaPercent: 80,
    answerTime: 20,
    aht: 6,
    shrinkage: 20,
    occupancy: 85,
  });

  // Handle service level changes (debounced via component)
  const handleServiceLevelChange = useCallback((params: ServiceLevelParams) => {
    setServiceLevelParams(params);
    // In a real app, this would trigger a recalculation API call
    console.log('Service level params changed:', params);
  }, []);

  // Handle forecast creation
  const handleCreateForecast = async (type: 'HISTORICAL' | 'CSV' | 'HISTORICAL_CSV', data?: any) => {
    if (!user?.organizationId) {
      console.error('No organization ID found');
      return;
    }

    // Handle Historical CSV upload (daily data to be disaggregated)
    if (type === 'HISTORICAL_CSV' && data?.file instanceof File) {
      console.log('Creating forecast from Historical CSV upload:', data.file.name);

      // Parse CSV to extract date range from Date column
      const csvText = await data.file.text();
      const lines = csvText.split('\n').filter((line: string) => {
        const trimmed = line.trim();
        return trimmed && !trimmed.startsWith('#');
      });

      if (lines.length < 2) {
        alert('CSV file must have at least a header row and one data row');
        return;
      }

      // Find date column index
      const headers = lines[0].split(',').map((h: string) => h.trim().toLowerCase());
      const dateIdx = headers.findIndex((h: string) => h === 'date' || h.includes('date'));

      if (dateIdx < 0) {
        alert('CSV must have a "Date" column for historical data');
        return;
      }

      // Parse dates
      const dates: Date[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',');
        if (values[dateIdx]) {
          const dateStr = values[dateIdx].trim().replace(/^["']|["']$/g, '');
          const d = new Date(dateStr);
          if (!isNaN(d.getTime())) {
            dates.push(d);
          }
        }
      }

      if (dates.length === 0) {
        alert('No valid dates found in CSV');
        return;
      }

      // Get historical date range from CSV
      dates.sort((a, b) => a.getTime() - b.getTime());
      const historicalStartDate = dates[0];
      const historicalEndDate = dates[dates.length - 1];

      // Calculate FUTURE dates for the forecast predictions
      // Start from today and predict 12 weeks into the future
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // CRITICAL: Calculate historicalWeeks from CSV START to TODAY (not CSV end)
      // This ensures the backend query (forecast.startDate - historicalWeeks*7) reaches all imported data
      const daysFromCsvStartToToday = Math.ceil((today.getTime() - historicalStartDate.getTime()) / (1000 * 60 * 60 * 24));
      const historicalWeeks = Math.max(1, Math.ceil(daysFromCsvStartToToday / 7));

      console.log(`Historical CSV date range: ${historicalStartDate.toISOString().split('T')[0]} to ${historicalEndDate.toISOString().split('T')[0]}`);
      console.log(`Days from CSV start to today: ${daysFromCsvStartToToday}, historicalWeeks: ${historicalWeeks}`);
      console.log(`Channels: ${data.channels?.join(', ')}`);
      const forecastStartDate = today.toISOString().split('T')[0];

      const forecastEndDate = new Date(today);
      forecastEndDate.setDate(forecastEndDate.getDate() + 12 * 7); // 12 weeks forward
      const forecastEndDateStr = forecastEndDate.toISOString().split('T')[0];

      console.log(`Creating forward-looking forecast: ${forecastStartDate} to ${forecastEndDateStr} (using ${historicalWeeks} weeks of imported historical data)`);

      // Create a forecast with FUTURE dates for predictions
      const forecastDto = {
        name: `Forecast from Historical Data ${new Date().toLocaleDateString()}`,
        organizationId: user.organizationId,
        startDate: forecastStartDate,
        endDate: forecastEndDateStr,
        forecastWeeks: 12,
        historicalWeeks: historicalWeeks, // Use the actual historical data range
        interval: data.intervalMinutes || 15,
        modelType: 'HISTORICAL_AVERAGE' as const,
        modelParameters: {
          historicalWeeks: historicalWeeks,
          weightDecay: 0.95,
          confidenceLevel: 0.95,
          importedFromHistoricalCSV: true,
          channels: data.channels,
          historicalDataRange: {
            start: historicalStartDate.toISOString().split('T')[0],
            end: historicalEndDate.toISOString().split('T')[0],
          },
        },
      };

      generateForecast(forecastDto, {
        onSuccess: (forecast) => {
          console.log('Forward-looking forecast created, now importing historical CSV data for training:', forecast.id);

          // Import the historical CSV data as training data
          importHistoricalCSV(
            {
              id: forecast.id,
              file: data.file,
              intervalMinutes: data.intervalMinutes || 15,
            },
            {
              onSuccess: (importResult) => {
                console.log('Historical CSV import successful:', importResult);

                // Now regenerate the forecast using the imported historical data
                console.log('Regenerating forecast with imported historical data (async)...');
                console.log(`Using historicalWeeks: ${historicalWeeks} to span from CSV start to today`);

                // Use async regeneration to handle large datasets without timeout
                regenerateAsync(
                  {
                    id: forecast.id,
                    dto: {
                      parameters: {
                        historicalWeeks: historicalWeeks,
                        weightDecay: 0.95, // Gentle decay: 0.95^12weeks = 0.54 (recent data weighted higher but all data matters)
                        confidenceLevel: 0.95,
                        useImportedData: true,
                        channels: data.channels,
                      },
                      regenerate: true, // Trigger regeneration using imported historical data
                    },
                  },
                  {
                    onSuccess: (regenerationResult) => {
                      console.log('Forecast regenerated with imported data (async):', regenerationResult);
                      alert(`Forecast generated successfully!\n\nImported ${importResult.imported} historical data points across ${importResult.channels?.join(', ')} channels.\n\nGenerated ${regenerationResult.dataPointsGenerated} prediction data points.\n\nForecast period: ${forecastStartDate} to ${forecastEndDateStr}\nHistorical training data: ${importResult.dateRange?.start?.split('T')[0]} to ${importResult.dateRange?.end?.split('T')[0]}`);
                      setShowCreateModal(false);

                      // Set selected date to start of forecast period (today)
                      setSelectedDate(today);
                      setSelectedForecastId(forecast.id);
                    },
                    onError: (regenError: any) => {
                      console.error('Forecast regeneration failed, but import succeeded:', regenError);
                      // Still show partial success
                      alert(`Historical data imported but forecast regeneration had issues.\n\nImported ${importResult.imported} data points.\n\nError: ${regenError.message || 'Unknown error'}\n\nYou may need to manually refresh the forecast.`);
                      setShowCreateModal(false);
                      setSelectedDate(today);
                      setSelectedForecastId(forecast.id);
                    },
                  }
                );
              },
              onError: (importError: any) => {
                console.error('Historical CSV import failed:', importError);
                const errorMessage = importError?.response?.data?.message || importError?.message || 'Unknown error';
                alert(`Forecast created but historical CSV import failed: ${errorMessage}`);
                setShowCreateModal(false);
                setSelectedForecastId(forecast.id);
              },
            }
          );
        },
        onError: (error: any) => {
          console.error('Failed to create forecast for historical CSV import:', error);
          const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
          alert(`Failed to create forecast: ${errorMessage}`);
        },
      });

      return;
    }

    // Handle CSV upload separately
    if (type === 'CSV' && data instanceof File) {
      console.log('Creating forecast from CSV upload:', data.name);

      // Parse CSV to extract date range
      const csvText = await data.text();
      const lines = csvText.split('\n').filter((line: string) => {
        const trimmed = line.trim();
        return trimmed && !trimmed.startsWith('#');
      });

      if (lines.length < 2) {
        alert('CSV file must have at least a header row and one data row');
        return;
      }

      // Parse timestamps to get date range
      const timestamps: Date[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',');
        if (values[0]) {
          const ts = new Date(values[0].trim().replace(/^["']|["']$/g, ''));
          if (!isNaN(ts.getTime())) {
            timestamps.push(ts);
          }
        }
      }

      if (timestamps.length === 0) {
        alert('No valid timestamps found in CSV');
        return;
      }

      // Sort timestamps and get date range
      timestamps.sort((a, b) => a.getTime() - b.getTime());
      const startDate = timestamps[0].toISOString().split('T')[0];
      const endDate = timestamps[timestamps.length - 1].toISOString().split('T')[0];

      console.log(`CSV date range: ${startDate} to ${endDate}, ${timestamps.length} data points`);

      // Create a basic forecast first with the CSV date range
      const forecastDto = {
        name: `CSV Import ${new Date().toLocaleDateString()}`,
        organizationId: user.organizationId,
        startDate,
        endDate,
        forecastWeeks: 1, // Will be overwritten by CSV data
        historicalWeeks: 1,
        interval: 15,
        modelType: 'HISTORICAL_AVERAGE' as const,
        modelParameters: {
          historicalWeeks: 1,
          weightDecay: 0.95,
          confidenceLevel: 0.95,
          importedFromCSV: true,
        },
      };

      console.log('Creating placeholder forecast for CSV import:', forecastDto);

      generateForecast(forecastDto, {
        onSuccess: (forecast) => {
          console.log('Placeholder forecast created, now importing CSV data:', forecast.id);

          // Now import the CSV data into the forecast
          importCSV(
            { id: forecast.id, file: data },
            {
              onSuccess: (importResult) => {
                console.log('CSV import successful:', importResult);
                alert(`Forecast created successfully!\n\nImported ${importResult.imported} data points from CSV.${importResult.errors?.length > 0 ? `\n\nWarnings:\n${importResult.errors.join('\n')}` : ''}`);
                setShowCreateModal(false);

                // Fix race condition: Set selected date from forecast start date BEFORE setting forecast ID
                // This ensures the chart displays imported data immediately
                if (forecast.startDate) {
                  const forecastStartDate = new Date(forecast.startDate);
                  console.log('Setting selected date to forecast start date:', forecastStartDate);
                  setSelectedDate(forecastStartDate);
                }

                setSelectedForecastId(forecast.id);
              },
              onError: (importError: any) => {
                console.error('CSV import failed:', importError);
                const errorMessage = importError?.response?.data?.message || importError?.message || 'Unknown error';
                alert(`Forecast created but CSV import failed: ${errorMessage}\n\nThe forecast exists but has no data. You can try importing the CSV again from the Actions menu.`);
                setShowCreateModal(false);
                setSelectedForecastId(forecast.id);
              },
            }
          );
        },
        onError: (error: any) => {
          console.error('Failed to create forecast for CSV import:', error);
          const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
          alert(`Failed to create forecast: ${errorMessage}`);
        },
      });

      return;
    }

    // Handle HISTORICAL forecast creation (existing logic)
    // Use data from modal if provided, otherwise use defaults
    const forecastWeeks = data?.forecastWeeks || 8;
    const historicalWeeks = data?.historicalWeeks || 12;
    const modelType = data?.modelType || 'HISTORICAL_AVERAGE';

    // Calculate date range
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + 1); // Start tomorrow
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + (forecastWeeks * 7));

    const forecastDto = {
      name: `${modelType} Forecast ${new Date().toLocaleDateString()}`,
      organizationId: user.organizationId,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      forecastWeeks,
      historicalWeeks,
      interval: 15, // 15 minute intervals
      modelType: modelType as 'HISTORICAL_AVERAGE' | 'ERLANG_C' | 'LINEAR_REGRESSION' | 'SEASONAL_DECOMP',
      modelParameters: {
        historicalWeeks,
        weightDecay: 0.95,
        confidenceLevel: 0.95,
        aht: serviceLevelParams.aht * 60, // Convert minutes to seconds
        targetServiceLevel: serviceLevelParams.slaPercent,
        targetAnswerTime: serviceLevelParams.answerTime,
        shrinkage: serviceLevelParams.shrinkage,
      },
    };

    console.log('Creating forecast with DTO:', forecastDto);

    generateForecast(forecastDto, {
      onSuccess: (forecast) => {
        console.log('Forecast created successfully:', forecast);
        alert(`Forecast "${forecast.name}" created successfully! ID: ${forecast.id}`);
        setShowCreateModal(false);
        setSelectedForecastId(forecast.id);
      },
      onError: (error: any) => {
        console.error('Failed to create forecast:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        alert(`Failed to create forecast: ${errorMessage}\n\nPlease check the console for details.`);
      },
    });
  };

  // Handle forecast shape editing
  const handleSaveForecastShape = (edits: {
    dateRangeEdits?: Array<{
      startDate: string;
      endDate: string;
      editType: DateRangeEditType;
      value: number;
      reason?: string;
    }>;
    edits?: Array<{
      timestamp: string;
      newValue: number;
      reason?: string;
    }>;
    applyToFutureDays?: boolean;
  }) => {
    if (!currentForecast) {
      alert('No forecast selected');
      return;
    }

    console.log('Saving forecast shape adjustments:', edits);

    editForecastValues(
      {
        id: currentForecast.id,
        dto: edits,
      },
      {
        onSuccess: () => {
          alert('Forecast updated successfully!');
          setShowShapeEditor(false);
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
          alert(`Failed to update forecast: ${errorMessage}`);
        },
      }
    );
  };

  // Handle saving holidays
  const handleSaveHolidays = (holidays: any[]) => {
    console.log('Saving holidays:', holidays);
    // TODO: Implement API call to save holidays to forecast
    // This would call an endpoint like: PUT /forecasts/:id/holidays
    alert(`Successfully saved ${holidays.length} holidays for forecast adjustment`);
  };

  // Handle generating shifts from forecast
  const handleGenerateShifts = async (config: any) => {
    if (!selectedForecastId) {
      alert('Please select a forecast first');
      return;
    }

    setIsGeneratingShifts(true);
    try {
      console.log('Generating shifts from forecast:', config);

      // Get shift start times from customShifts array (15-minute interval format)
      // Falls back to preset shift types if customShifts is empty
      let shiftStartTimes: string[] = [];

      if (config.customShifts && config.customShifts.length > 0) {
        // Use custom shifts with 15-minute interval start times
        shiftStartTimes = config.customShifts.map((shift: any) => shift.startTime);
      } else if (config.shiftTypes && config.shiftTypes.length > 0) {
        // Fallback: Map preset shift types to start times
        const shiftStartTimeMap: Record<string, string> = {
          'MORNING': '07:00',
          'MID_DAY': '09:00',
          'AFTERNOON': '11:00',
          'EVENING': '15:00',
          'OVERNIGHT': '23:00',
        };
        shiftStartTimes = config.shiftTypes.map((type: string) => shiftStartTimeMap[type] || '09:00');
      } else {
        shiftStartTimes = ['09:00'];
      }

      // Build shift data with per-shift duration and agent count
      // CRITICAL: Only include agentsNeeded if explicitly set (> 1) from optimization
      // Otherwise, let the backend calculate from actual forecast requirements
      // This prevents overriding backend calculations with hardcoded values
      const shiftsData = config.customShifts?.map((shift: any) => ({
        startTime: shift.startTime,
        duration: shift.duration || config.shiftDuration || 8,
        // Only include agentsNeeded if it's a meaningful value from optimization (> 1)
        // A value of 0 or 1 likely means "not set" and backend should calculate
        ...(shift.agentsNeeded > 1 ? { agentsNeeded: shift.agentsNeeded } : {}),
      })) || shiftStartTimes.map((startTime: string) => ({
        startTime,
        duration: config.shiftDuration || 8,
        // Don't send agentsNeeded - let backend calculate from requirements
      }));

      // Call the shift planning auto-generate endpoint
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const response = await fetch(
        `${baseUrl}/forecasts/${selectedForecastId}/shift-planning/shifts/auto-generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            shiftDuration: config.shiftDuration || 8,
            shiftStartTimes,
            shifts: shiftsData,
            minAgentsPerShift: 1,
            coverageBuffer: config.coverageBuffer || 10,
            startDate: config.startDate,
            endDate: config.endDate,
            daysOfWeek: config.daysOfWeek,
            channel: config.channel !== 'ALL' ? config.channel : undefined,
            teamId: config.teamId,
            createdBy: user?.id,
          }),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to generate shifts';
        try {
          const error = await response.json();
          errorMessage = error.message || error.error || JSON.stringify(error);
        } catch {
          // Response wasn't JSON
          const textError = await response.text();
          errorMessage = textError || `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Shifts generated:', result);

      // First close the modal and clear loading state
      setShowShiftGeneration(false);
      setIsGeneratingShifts(false);

      // Navigate to shift planning page with success state
      navigate('/forecasts/shift-planning', {
        state: {
          forecastId: selectedForecastId,
          successMessage: `Successfully generated ${result.shiftsCreated || result.length || ''} shifts!`,
          generatedAt: new Date().toISOString(),
        },
      });
    } catch (error) {
      console.error('Error generating shifts:', error);
      setIsGeneratingShifts(false);

      let errorMessage = 'Unknown error';
      if (error instanceof Error) {
        if (error.message === 'Load failed' || error.message === 'Failed to fetch') {
          errorMessage = 'Cannot connect to the API server. Please ensure the server is running.';
        } else {
          errorMessage = error.message;
        }
      }
      alert(`Error generating shifts: ${errorMessage}`);
    }
  };

  // Handle auto-fill gap
  const handleAutoFillGap = (interval: string, gap: number) => {
    console.log('Auto-filling gap at', interval, 'with', Math.abs(gap), 'agents');
    // In a real app, this would:
    // 1. Find available agents on EMAIL/CHAT with PHONE skill
    // 2. Move them to PHONE for this interval
    // 3. Update the schedule via API
    // 4. Refresh the staffing grid
    alert(`Auto-fill feature would move ${Math.abs(gap)} agents from EMAIL/CHAT to PHONE at ${interval}`);
  };

  // Transform forecast data for chart - filter based on viewMode
  const chartData = useMemo(() => {
    if (!forecastData || forecastData.length === 0) return [];

    // For demo account or when no timestamps, just take first 96 points
    if (isDemoAccount || !forecastData[0]?.timestamp) {
      return forecastData.slice(0, 96);
    }

    // Helper to get local date string (YYYY-MM-DD) from a timestamp
    // This ensures we use the user's local timezone for date grouping
    const getLocalDateString = (ts: Date | string): string => {
      const d = ts instanceof Date ? ts : new Date(ts);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Get the selected date as local date string
    const selectedDateStr = getLocalDateString(selectedDate);

    if (viewMode === 'DAILY') {
      // DAILY: Filter data points for the selected date (using local time)
      const filteredData = forecastData.filter((point) => {
        if (!point.timestamp) return false;
        return getLocalDateString(point.timestamp) === selectedDateStr;
      });

      // Sort by timestamp to ensure proper ordering (00:00 to 23:45)
      return [...filteredData].sort((a, b) => {
        const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
        const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
        return timeA - timeB;
      });
    }

    // WEEKLY or MONTHLY: Aggregate data by day
    const aggregatedByDay = new Map<string, { total: number; count: number; date: Date }>();

    // Calculate date ranges using local time
    let startDateStr: string;
    let endDateStr: string;

    if (viewMode === 'WEEKLY') {
      // Get the week containing selectedDate (Sunday to Saturday) using local time
      const dayOfWeek = selectedDate.getDay(); // 0 = Sunday (local time)
      const weekStartDate = new Date(selectedDate);
      weekStartDate.setDate(selectedDate.getDate() - dayOfWeek);
      weekStartDate.setHours(0, 0, 0, 0);
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 6);

      startDateStr = getLocalDateString(weekStartDate);
      endDateStr = getLocalDateString(weekEndDate);
    } else {
      // MONTHLY: Get the entire month containing selectedDate using local time
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth();
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0); // Last day of month

      startDateStr = getLocalDateString(monthStart);
      endDateStr = getLocalDateString(monthEnd);
    }

    // Debug logging - comprehensive info for debugging monthly/weekly views
    console.log(`[ChartData] viewMode=${viewMode}, selectedDate=${selectedDate.toISOString()}`);
    console.log(`[ChartData] Filter range: ${startDateStr} to ${endDateStr}`);

    // Log the actual date range of the forecast data
    if (forecastData.length > 0) {
      const allDates = forecastData
        .filter(p => p.timestamp)
        .map(p => getLocalDateString(p.timestamp))
        .sort();
      const uniqueDates = [...new Set(allDates)];
      console.log(`[ChartData] Forecast data covers ${uniqueDates.length} unique days: ${uniqueDates[0]} to ${uniqueDates[uniqueDates.length - 1]}`);
      console.log(`[ChartData] Total forecast data points: ${forecastData.length}`);
    }

    forecastData.forEach((point) => {
      if (!point.timestamp) return;

      const pointDateStr = getLocalDateString(point.timestamp);

      // Filter by date range (string comparison works for YYYY-MM-DD format)
      if (pointDateStr < startDateStr || pointDateStr > endDateStr) return;

      const existing = aggregatedByDay.get(pointDateStr);
      if (existing) {
        existing.total += point.predictedValue;
        existing.count += 1;
      } else {
        aggregatedByDay.set(pointDateStr, {
          total: point.predictedValue,
          count: 1,
          date: new Date(pointDateStr + 'T12:00:00'), // Use noon local time to avoid date shift
        });
      }
    });

    console.log(`[ChartData] Aggregated ${aggregatedByDay.size} days of data within filter range`);
    if (aggregatedByDay.size > 0) {
      const aggregatedDates = Array.from(aggregatedByDay.keys()).sort();
      console.log(`[ChartData] Aggregated dates: ${aggregatedDates.join(', ')}`);
    }

    // Convert aggregated data to chart format
    const aggregatedData = Array.from(aggregatedByDay.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([dateKey, data]) => ({
        interval: new Date(dateKey + 'T12:00:00').toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        predictedValue: Math.round(data.total),
        timestamp: data.date,
      }));

    return aggregatedData;
  }, [forecastData, selectedDate, isDemoAccount, viewMode]);

  // Auto-detect the data interval from forecast timestamps
  const detectedInterval = useMemo((): number => {
    if (!forecastData || forecastData.length < 2) return 15;
    const timestamps = forecastData
      .filter((dp: any) => dp.timestamp)
      .map((dp: any) => new Date(dp.timestamp).getTime())
      .sort((a: number, b: number) => a - b);
    if (timestamps.length < 2) return 15;
    const deltaCounts = new Map<number, number>();
    for (let i = 0; i < Math.min(timestamps.length - 1, 200); i++) {
      const delta = Math.round((timestamps[i + 1] - timestamps[i]) / (1000 * 60));
      if (delta > 0 && delta <= 1440) deltaCounts.set(delta, (deltaCounts.get(delta) || 0) + 1);
    }
    if (deltaCounts.size === 0) return 15;
    let best = 0, maxC = 0;
    deltaCounts.forEach((c, d) => { if (c > maxC) { maxC = c; best = d; } });
    if (best <= 17) return 15;
    if (best <= 45) return 30;
    if (best <= 90) return 60;
    return best;
  }, [forecastData]);

  // Transform for Net Staffing Grid
  const gridForecastData = chartData.map((point) => ({
    time: point.interval,
    volume: point.predictedValue,
  }));

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  // Handle delete forecast
  const handleDeleteForecast = () => {
    if (!currentForecast) return;

    if (confirm(`Are you sure you want to delete "${currentForecast.name}"? This action cannot be undone.`)) {
      deleteForecast(currentForecast.id, {
        onSuccess: () => {
          alert('Forecast deleted successfully');
          setSelectedForecastId(null);
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
          alert(`Failed to delete forecast: ${errorMessage}`);
        },
      });
    }
  };

  // Handle publish forecast
  const handlePublishForecast = () => {
    if (!currentForecast) return;

    if (confirm(`Publish "${currentForecast.name}"?\n\nThis will:\n• Make the forecast available for scheduling\n• Lock editing (requires unpublish to edit again)\n• Begin tracking accuracy\n\nContinue?`)) {
      publishForecast(
        { id: currentForecast.id, force: false },
        {
          onSuccess: () => {
            alert('Forecast published successfully!');
          },
          onError: (error: any) => {
            const errorData = error?.response?.data;
            const errorMessage = errorData?.message || error?.message || 'Unknown error';

            // Check if it's a validation error that can be forced
            if (errorData?.canForce && errorData?.errors) {
              const errors = errorData.errors.join('\n');
              if (confirm(`Validation warnings:\n\n${errors}\n\nDo you want to force publish anyway?`)) {
                publishForecast(
                  { id: currentForecast.id, force: true },
                  {
                    onSuccess: () => {
                      alert('Forecast published successfully (forced)!');
                    },
                    onError: (forceError: any) => {
                      const forceErrorMessage = forceError?.response?.data?.message || forceError?.message || 'Unknown error';
                      alert(`Failed to force publish: ${forceErrorMessage}`);
                    },
                  }
                );
              }
            } else {
              alert(`Failed to publish forecast: ${errorMessage}`);
            }
          },
        }
      );
    }
  };

  // Handle unpublish forecast
  const handleUnpublishForecast = () => {
    if (!currentForecast) return;

    if (confirm(`Unpublish "${currentForecast.name}"?\n\nThis will:\n• Return the forecast to draft status\n• Allow editing again\n• Remove it from active scheduling\n\nContinue?`)) {
      unpublishForecast(currentForecast.id, {
        onSuccess: () => {
          alert('Forecast unpublished successfully!');
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
          alert(`Failed to unpublish forecast: ${errorMessage}`);
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Forecasting & Staffing</h1>
          <p className="text-gray-600">
            AI-powered volume forecasting with real-time staffing analysis
          </p>
        </div>

        {/* Forecast Selector - Only show for non-demo accounts with forecasts */}
        {!isDemoAccount && forecasts && forecasts.length > 0 && (
          <div className="mx-4 min-w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Forecast
            </label>
            <select
              value={selectedForecastId || ''}
              onChange={(e) => setSelectedForecastId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            >
              {forecasts.map((forecast) => (
                <option key={forecast.id} value={forecast.id}>
                  {forecast.name} - {new Date(forecast.startDate).toLocaleDateString()} to {new Date(forecast.endDate).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          {/* Primary Actions */}
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={isGenerating}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            <span>{isGenerating ? 'Generating...' : 'Create Forecast'}</span>
          </button>

          {!isDemoAccount && currentForecast && currentForecast.status !== 'PUBLISHED' && currentForecast.status !== 'ACTIVE' && (
            <button
              onClick={handlePublishForecast}
              disabled={isPublishing}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="h-4 w-4" />
              <span>{isPublishing ? 'Publishing...' : 'Publish'}</span>
            </button>
          )}

          {!isDemoAccount && currentForecast && (currentForecast.status === 'PUBLISHED' || currentForecast.status === 'ACTIVE') && (
            <button
              onClick={handleUnpublishForecast}
              disabled={isUnpublishing}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              <span>{isUnpublishing ? 'Unpublishing...' : 'Unpublish'}</span>
            </button>
          )}

          {/* Actions Dropdown */}
          {currentForecast && (
            <div className="relative">
              <button
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <MoreVertical className="h-4 w-4" />
                <span>Actions</span>
              </button>

              {showActionsMenu && (
                <>
                  {/* Backdrop to close menu */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowActionsMenu(false)}
                  />

                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                    <div className="py-1">
                      {/* Export */}
                      <button
                        onClick={() => {
                          exportCSV(currentForecast.id);
                          setShowActionsMenu(false);
                        }}
                        disabled={isExporting}
                        className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download className="h-4 w-4" />
                        <span>{isExporting ? 'Exporting...' : 'Download CSV'}</span>
                      </button>

                      {!isDemoAccount && (
                        <>
                          <div className="border-t border-gray-100 my-1" />

                          {/* Edit Shape */}
                          <button
                            onClick={() => {
                              setShowShapeEditor(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Edit className="h-4 w-4" />
                            <span>Edit Shape</span>
                          </button>

                          {/* Generate Shifts */}
                          <button
                            onClick={() => {
                              setShowShiftGeneration(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Users className="h-4 w-4" />
                            <span>Generate Shifts</span>
                          </button>

                          {/* Manage Holidays */}
                          <button
                            onClick={() => {
                              setShowHolidayManagement(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Calendar className="h-4 w-4" />
                            <span>Manage Holidays</span>
                          </button>

                          {/* Mark Outlier Days */}
                          <button
                            onClick={() => {
                              setShowOutlierDetection(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <AlertTriangle className="h-4 w-4" />
                            <span>Mark Outlier Days</span>
                          </button>

                          <div className="border-t border-gray-100 my-1" />

                          {/* Version History */}
                          <button
                            onClick={() => {
                              setIsVersionHistoryOpen(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <History className="h-4 w-4" />
                            <span>Version History</span>
                          </button>

                          <div className="border-t border-gray-100 my-1" />

                          {/* Delete */}
                          <button
                            onClick={() => {
                              handleDeleteForecast();
                              setShowActionsMenu(false);
                            }}
                            disabled={isDeleting}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>{isDeleting ? 'Deleting...' : 'Delete Forecast'}</span>
                          </button>
                        </>
                      )}

                      {isDemoAccount && (
                        <>
                          <div className="border-t border-gray-100 my-1" />

                          {/* Edit Shape for demo */}
                          <button
                            onClick={() => {
                              setShowShapeEditor(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Edit className="h-4 w-4" />
                            <span>Edit Shape</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoadingForecasts && (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading forecasts...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoadingForecasts && (!forecasts || forecasts.length === 0) && (
        <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
          <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Forecasts Yet</h3>
          <p className="text-gray-600 mb-6">
            Create your first forecast to start predicting contact volume and staffing requirements.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
          >
            Create Your First Forecast
          </button>
        </div>
      )}

      {/* Main Content - Only show when we have forecasts */}
      {forecasts && forecasts.length > 0 && (
        <div className="space-y-6">
          {/* Loading data points */}
          {isLoadingDataPoints && (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
              <p className="text-gray-600">Loading forecast data...</p>
            </div>
          )}

          {/* Service Level Controls - Top */}
          {!isLoadingDataPoints && (
            <>
              <ServiceLevelControls
                initialSLAPercent={serviceLevelParams.slaPercent}
                initialAnswerTime={serviceLevelParams.answerTime}
                initialAHT={serviceLevelParams.aht}
                initialShrinkage={serviceLevelParams.shrinkage}
                initialOccupancy={serviceLevelParams.occupancy}
                onChange={handleServiceLevelChange}
              />

              {/* Forecast vs Actual Chart - Middle */}
              <ForecastVsActualChart
                data={chartData}
                interval={selectedInterval}
                onIntervalChange={setSelectedInterval}
                selectedChannel={selectedChannel}
                onChannelChange={setSelectedChannel}
                selectedTeam={selectedTeam}
                onTeamChange={setSelectedTeam}
                selectedShift={selectedShift}
                onShiftChange={setSelectedShift}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                variancePercent={variancePercent}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                onPreviousDay={handlePreviousDay}
                onNextDay={handleNextDay}
                onToday={handleToday}
              />

              {/* Weekly/Monthly Collapsible Table - Show when not in daily view */}
              {(viewMode === 'WEEKLY' || viewMode === 'MONTHLY') && (
                <CollapsibleForecastTable
                  viewMode={viewMode}
                  forecastData={forecastData}
                  selectedChannel={selectedChannel}
                  selectedTeam={selectedTeam}
                  selectedShift={selectedShift}
                  serviceLevelParams={serviceLevelParams}
                />
              )}

              {/* Net Staffing Grid - Bottom (The Star Feature) - Only show in daily view */}
              {viewMode === 'DAILY' && (
                <NetStaffingGrid
                  forecastData={gridForecastData}
                  scheduledData={scheduledData}
                  serviceLevelParams={serviceLevelParams}
                  onAutoFill={handleAutoFillGap}
                  intervalMinutes={detectedInterval}
                />
              )}

              {/* Daily Accuracy Section */}
              <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowAccuracySection(!showAccuracySection)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <BarChart3 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-900">Forecast Accuracy</h3>
                      <p className="text-sm text-gray-600">
                        {dailyAccuracyData?.overall
                          ? `${dailyAccuracyData.overall.accuracyScore.toFixed(1)}% overall accuracy (${dailyAccuracyData.daily.length} days tracked)`
                          : 'View daily accuracy breakdown and trends'}
                      </p>
                    </div>
                  </div>
                  {showAccuracySection ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>

                {showAccuracySection && (
                  <div className="p-4">
                    {isLoadingDailyAccuracy ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">Loading accuracy data...</span>
                      </div>
                    ) : dailyAccuracyData ? (
                      <DailyAccuracyTable data={dailyAccuracyData} />
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No accuracy data available. Sync actual values to see the accuracy breakdown.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Forecast Assumptions Section */}
              <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowAssumptionsSection(!showAssumptionsSection)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-900">Forecast Assumptions</h3>
                      <p className="text-sm text-gray-600">
                        {assumptions && assumptions.length > 0
                          ? `${assumptions.length} assumption${assumptions.length !== 1 ? 's' : ''} applied to this forecast`
                          : 'Add growth factors, efficiency adjustments, and event impacts'}
                      </p>
                    </div>
                  </div>
                  {showAssumptionsSection ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>

                {showAssumptionsSection && (
                  <div className="p-4 space-y-6">
                    <>
                      {/* Factor Breakdown Visualization */}
                      <ForecastFactorBreakdown
                        data={factorBreakdown || { baseVolume: 0, factors: [], finalVolume: 0 }}
                        isLoading={isLoadingFactorBreakdown}
                      />

                      {/* Assumptions Management Panel */}
                      <ForecastAssumptionsPanel
                        forecastId={selectedForecastId || ''}
                        assumptions={assumptions || []}
                        isLoading={isLoadingAssumptions || isCreatingAssumption || isUpdatingAssumption || isDeletingAssumption}
                        onCreateAssumption={(dto) => {
                          if (selectedForecastId) {
                            createAssumption({ forecastId: selectedForecastId, dto });
                          }
                        }}
                        onUpdateAssumption={(assumptionId, dto) => {
                          if (selectedForecastId) {
                            updateAssumption({ forecastId: selectedForecastId, assumptionId, dto });
                          }
                        }}
                        onDeleteAssumption={(assumptionId) => {
                          if (selectedForecastId) {
                            deleteAssumptionMutation({ forecastId: selectedForecastId, assumptionId });
                          }
                        }}
                      />
                    </>
                  </div>
                )}
              </div>

              {/* AI Assistant Section */}
              <div className="bg-white border-2 border-purple-200 rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <MessageSquare className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">AI Forecast Assistant</h3>
                        <p className="text-sm text-purple-100">
                          Get insights, detect anomalies, and run scenario planning
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowAIAssistant(true)}
                      className="px-4 py-2 bg-white text-purple-700 rounded-lg font-medium hover:bg-purple-50 transition-colors flex items-center space-x-2"
                    >
                      <MessageSquare className="h-4 w-4" />
                      <span>Open AI Assistant</span>
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4 bg-purple-50">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">🔍</span>
                        <h4 className="font-semibold text-gray-900">Anomaly Detection</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        Automatically identify unusual spikes or dips in your forecast data
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">📊</span>
                        <h4 className="font-semibold text-gray-900">Scenario Planning</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        Model "what if" scenarios for volume changes and staffing impacts
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">💡</span>
                        <h4 className="font-semibold text-gray-900">Smart Insights</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        Get recommendations to reduce shrinkage and improve occupancy
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Modals */}
      <CreateForecastModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateForecast={handleCreateForecast}
        isLoading={isGenerating || isImportingCSV || isImportingHistoricalCSV}
      />

      {currentForecast && (
        <ForecastShapeEditor
          isOpen={showShapeEditor}
          onClose={() => setShowShapeEditor(false)}
          forecastData={forecastData as any}
          forecastStartDate={currentForecast.startDate?.split('T')[0] || ''}
          forecastEndDate={currentForecast.endDate?.split('T')[0] || ''}
          onSave={handleSaveForecastShape}
          isSaving={isEditingForecast}
        />
      )}

      <ForecastAIAssistant
        isOpen={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
      />

      {/* Outlier Detection Modal */}
      {currentForecast && (
        <OutlierDetectionModal
          isOpen={showOutlierDetection}
          onClose={() => setShowOutlierDetection(false)}
          forecastId={currentForecast.id}
          forecastData={forecastData}
        />
      )}

      {/* Holiday Management Modal */}
      {currentForecast && (
        <HolidayManagementModal
          isOpen={showHolidayManagement}
          onClose={() => setShowHolidayManagement(false)}
          forecastId={currentForecast.id}
          onSaveHolidays={handleSaveHolidays}
        />
      )}

      {/* Shift Generation Modal */}
      {currentForecast && (
        <ShiftGenerationModal
          isOpen={showShiftGeneration}
          onClose={() => setShowShiftGeneration(false)}
          forecastId={currentForecast.id}
          forecastData={forecastData}
          serviceLevelParams={serviceLevelParams}
          onGenerateShifts={handleGenerateShifts}
          isLoading={isGeneratingShifts}
          forecastStartDate={currentForecast.startDate?.split('T')[0]}
          forecastEndDate={currentForecast.endDate?.split('T')[0]}
        />
      )}

      {/* Version History Sidebar */}
      {currentForecast && (
        <ForecastVersionHistorySidebar
          isOpen={isVersionHistoryOpen}
          onClose={() => setIsVersionHistoryOpen(false)}
          currentForecastId={currentForecast.id}
        />
      )}
    </div>
  );
}
